import { useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Building2,
  ClipboardList,
  FileCheck2,
  Landmark,
  MessagesSquare,
  Search,
  ShieldCheck,
} from "lucide-react";

import LinkButton from "./LinkButton.jsx";
import SectionHeader from "./SectionHeader.jsx";
import StatCard from "./StatCard.jsx";
import { issueCategories } from "./categories.js";

import heroImage from "./53691941021_32452563b0_k-1536x1023.jpg";
import communityImage from "./53692293039_82e4b561e9_o-1536x1140.jpg";
import massFlag from "./Flag_of_Massachusetts.svg";
import cambridgeImage from "./Things-to-Do-in-Cambridge-Massachusetts-scaled.jpg";
import bostonImage from "./downtown-cityscape-boston-massachusetts.avif";

const workflowCards = [
  {
    icon: MessagesSquare,
    title: "Consumer Guidance",
    text: "Helps consumers identify the issue, organize facts, and understand possible next steps.",
  },
  {
    icon: ClipboardList,
    title: "Complaint Preparation",
    text: "Collects contact information, business details, timelines, supporting records, and requested outcomes.",
  },
  {
    icon: FileCheck2,
    title: "Review Readiness",
    text: "Structures complaint information so it can be reviewed, followed up on, or referred appropriately.",
  },
  {
    icon: BarChart3,
    title: "Referral Support",
    text: "Helps identify when another local, state, or federal resource may be better suited to assist.",
  },
];

function filterAdditionalCategories(searchValue, categories) {
  const value = searchValue.trim().toLowerCase();

  if (!value) {
    return [];
  }

  return categories
    .filter((category) =>
      `${category.label} ${category.description}`.toLowerCase().includes(value)
    )
    .slice(0, 8);
}

export default function Home({ navigate }) {
  const [issueSearch, setIssueSearch] = useState("");

  const primaryCategories = issueCategories.slice(0, 8);
  const additionalCategories = issueCategories.slice(8);

  const visibleAdditionalCategories = useMemo(
    () => filterAdditionalCategories(issueSearch, additionalCategories),
    [issueSearch]
  );

  const customIssue = issueSearch.trim();

  const goToIssue = (event, issueId) => {
    event.preventDefault();
    navigate(`/start?issue=${issueId}`);
  };

  return (
    <>
      <section className="relative overflow-hidden border-b border-civic-line bg-civic-navy text-white">
        <img
          src={heroImage}
          alt="Cambridge community view"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-civic-navy via-civic-navy/90 to-civic-navy/55" />

        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.05fr_.95fr] lg:px-8 lg:py-20">
          <div className="flex flex-col justify-center">
            <div className="mb-5 inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white ring-1 ring-white/25">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Consumer assistance and complaint preparation
            </div>

            <h1 className="max-w-3xl text-4xl font-black leading-tight sm:text-5xl">
              Consumer assistance, complaint preparation, and referral support.
            </h1>

            <p className="mt-5 max-w-2xl text-lg leading-8 text-blue-50">
              Cambridge Consumer Solutions helps consumers organize disputes,
              understand practical next steps, and prepare complete complaint
              information for review or referral.
            </p>

            <div className="mt-5 rounded-md border border-white/20 bg-white/10 p-4 text-sm leading-6 text-blue-50 backdrop-blur">
              <div className="flex gap-3">
                <Landmark className="mt-1 h-5 w-5 shrink-0 text-white" />
                <p>
                  This portal helps consumers prepare information consistent
                  with the Cambridge Consumers’ Council complaint process,
                  including referrals to consumer protection resources and the
                  Massachusetts Attorney General’s consumer complaint options
                  when appropriate.
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <LinkButton href="/start" navigate={navigate}>
                Start guided review
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </LinkButton>

              <LinkButton href="/complaint" navigate={navigate} variant="secondary">
                Prepare complaint
              </LinkButton>
            </div>
          </div>

          <div className="rounded-md border border-white/20 bg-white/95 p-4 text-civic-navy shadow-panel">
            <div className="rounded-md bg-white p-5">
              <div className="mb-5 flex items-center gap-4 border-b border-civic-line pb-5">
                <img
                  src={massFlag}
                  alt="Massachusetts flag"
                  className="h-20 w-20 shrink-0 rounded-md border border-civic-line bg-white object-contain p-2"
                />

                <div>
                  <div className="text-sm font-bold text-civic-navy">
                    Consumer Protection Referral Support
                  </div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">
                    Organized intake and referral preparation for consumer
                    assistance review.
                  </div>
                </div>
              </div>

              <div className="grid gap-3">
                {[
                  ["Issue type", "Consumer complaint"],
                  ["Consumer action", "Attempted to resolve with business"],
                  ["Requested outcome", "Refund, repair, replacement, or explanation"],
                  ["Referral review", "Appropriate consumer protection resource"],
                  ["Next step", "Prepare documents and submit complaint"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="grid grid-cols-[.85fr_1.15fr] gap-4 rounded-md bg-slate-50 p-3 text-sm"
                  >
                    <span className="font-semibold text-slate-500">
                      {label}
                    </span>
                    <span className="font-bold text-civic-navy">{value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <div className="rounded-md bg-civic-sky p-3 text-center">
                  <Building2 className="mx-auto h-5 w-5 text-civic-blue" />
                  <div className="mt-2 text-xs font-bold text-civic-navy">
                    Referral
                  </div>
                </div>

                <div className="rounded-md bg-civic-sky p-3 text-center">
                  <ClipboardList className="mx-auto h-5 w-5 text-civic-blue" />
                  <div className="mt-2 text-xs font-bold text-civic-navy">
                    Timeline
                  </div>
                </div>

                <div className="rounded-md bg-civic-sky p-3 text-center">
                  <BadgeCheck className="mx-auto h-5 w-5 text-civic-blue" />
                  <div className="mt-2 text-xs font-bold text-civic-navy">
                    Review Ready
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            value={String(issueCategories.length)}
            label="Issue paths"
            detail="Guidance for common consumer matters."
          />
          <StatCard
            value="Written"
            label="Complaint preparation"
            detail="Consumers can organize facts, dates, and records before filing."
          />
          <StatCard
            value="Referral"
            label="Consumer resources"
            detail="Support for identifying appropriate consumer protection resources."
          />
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[.9fr_1.1fr] lg:px-8">
          <div>
            <SectionHeader
              eyebrow="Community Support"
              title="Clear, local, and approachable consumer assistance"
            >
              The portal gives consumers a calmer place to organize facts,
              understand options, and prepare information before review or
              referral.
            </SectionHeader>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <img
                src={cambridgeImage}
                alt="Cambridge Massachusetts street view"
                className="h-56 w-full rounded-md object-cover shadow-sm"
              />
              <img
                src={bostonImage}
                alt="Boston Massachusetts cityscape"
                className="h-56 w-full rounded-md object-cover shadow-sm"
              />
            </div>
          </div>

          <img
            src={communityImage}
            alt="Massachusetts community setting"
            className="h-full min-h-80 w-full rounded-md object-cover shadow-panel"
          />
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Portal Tools"
            title="A practical workflow for consumer assistance"
          >
            The portal combines guided triage, complaint preparation, referral
            information, and review-ready summaries in one responsive website.
          </SectionHeader>

          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {workflowCards.map(({ icon: Icon, title, text }) => (
              <article
                key={title}
                className="rounded-md border border-civic-line bg-white p-5 shadow-sm transition hover:border-civic-blue hover:shadow-panel"
              >
                <Icon className="h-6 w-6 text-civic-blue" aria-hidden="true" />
                <h3 className="mt-4 text-base font-bold text-civic-navy">
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Issue Types"
          title="Start with the category that best matches the dispute"
        />

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {primaryCategories.map((category) => (
            <a
              key={category.id}
              href={`/start?issue=${category.id}`}
              onClick={(event) => goToIssue(event, category.id)}
              className="focus-ring rounded-md border border-civic-line bg-white p-4 text-left shadow-sm transition hover:border-civic-blue hover:shadow-panel"
            >
              <div className="font-bold text-civic-navy">{category.label}</div>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {category.description}
              </p>
            </a>
          ))}
        </div>

        <div className="mt-8 rounded-md border border-civic-line bg-white p-5 shadow-sm">
          <label className="label" htmlFor="issueSearch">
            Search additional issue categories
          </label>

          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400"
              aria-hidden="true"
            />

            <input
              id="issueSearch"
              type="search"
              className="field pl-10"
              value={issueSearch}
              onChange={(event) => setIssueSearch(event.target.value)}
              placeholder="Type refund, auto, debt, housing, fraud, billing, repair, or another issue..."
            />
          </div>

          <p className="help-text">
            Search additional categories without displaying the full list on the
            page.
          </p>

          {customIssue && (
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {visibleAdditionalCategories.map((category) => (
                <a
                  key={category.id}
                  href={`/start?issue=${category.id}`}
                  onClick={(event) => goToIssue(event, category.id)}
                  className="focus-ring rounded-md border border-civic-line bg-slate-50 p-4 text-left transition hover:border-civic-blue hover:bg-civic-sky"
                >
                  <div className="font-bold text-civic-navy">
                    {category.label}
                  </div>

                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {category.description}
                  </p>
                </a>
              ))}

              {visibleAdditionalCategories.length === 0 && (
                <a
                  href="/start?issue=other-consumer-issue"
                  onClick={(event) => goToIssue(event, "other-consumer-issue")}
                  className="focus-ring rounded-md border border-civic-blue bg-civic-sky p-4 text-left"
                >
                  <div className="font-bold text-civic-navy">
                    Use "{customIssue}"
                  </div>

                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Select this as another consumer issue and describe it in the
                    guided review or complaint form.
                  </p>
                </a>
              )}
            </div>
          )}
        </div>

        <p className="mt-10 border-t border-civic-line pt-4 text-xs leading-5 text-slate-500">
          Website created by Degefe McCracken-Bradley. Cambridge Consumer
          Solutions helps consumers prepare complaint information and identify
          consumer protection resources. It does not provide legal advice.
        </p>
      </section>
    </>
  );
}
